import { ReactNode } from 'react';
import {
  FieldValues,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { observer } from 'mobx-react-lite';
import { ZodType } from 'zod';

interface FormProps<T extends FieldValues> {
  schema: ZodType<T, any, any>;
  onSubmit: SubmitHandler<T>;
  onBeforeSubmit?: (data: T) => Promise<boolean> | boolean;
  onError?: SubmitErrorHandler<T>;
  children: (methods: UseFormReturn<T>) => ReactNode;
  options?: UseFormProps<T>;
  id?: string;
  className?: string;
}

export const Form = observer(<T extends FieldValues>({
  schema,
  onSubmit,
  onBeforeSubmit,
  onError,
  children,
  options,
  id,
  className,
}: FormProps<T>) => {
  const methods = useForm<T>({
    ...options,
    resolver: zodResolver(schema),
  });

  const handleFormSubmit = async (data: T, event?: React.BaseSyntheticEvent) => {
    if (onBeforeSubmit) {
      const shouldSubmit = await onBeforeSubmit(data);
      if (!shouldSubmit) return;
    }
    onSubmit(data, event);
  };

  return (
    <form
      id={id}
      className={className}
      onSubmit={methods.handleSubmit(handleFormSubmit, onError)}
      noValidate
    >
      {children(methods)}
    </form>
  );
});
